import EventNames from '../../../config/eventNames';
import { getContainers, getServices } from '../../../infra/startup';
import { AuthContextEnum } from '../../../services/authTokenService';
import IEventService from '../../../services/eventService/IEventService';
import { IGrantService } from '../../../services/grants/IGrantService';
import { GrantOptionsType, GrantType } from '../../../services/grants/types';
import bindAllMethods from '../../../utils/bindAllMethods';
import { PromiseHelper } from '../../../utils/PromiseHelper';
import { GrantsInterfaceType } from '@clientos/types';
import { Refreshable } from '../../../utils/refreshable/IRefreshable';
import { IContainer } from '../../../core/containers';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ReactLike = any;

export class GrantsInterface extends Refreshable {
  private defaultKey = 'default';
  private initializedAuthContext: Map<
    AuthContextEnum | typeof this.defaultKey,
    boolean
  > = new Map();
  private promiseHelper = new PromiseHelper();

  private eventsService: IEventService;
  private accessControlContainer: IContainer;
  private grantService: IGrantService;

  constructor() {
    super();
    const services = getServices();
    const containers = getContainers();
    this.eventsService = services.eventService;
    this.accessControlContainer = containers.accessControlContainer;

    this.grantService = services.grantService;
    bindAllMethods(this);

    // new schema
    this.setRefreshMethods({
      init: this._initialize.bind(this),
      refresh: this.refresh.bind(this)
    });

    // old schema
    this.accessControlContainer.addInitServices(
      this._initialize,
      undefined,
      'GrantsInterface'
    );
    this.accessControlContainer.addRefreshServices(
      this.refresh,
      undefined,
      'GrantsInterface'
    );
  }

  getInterface(): GrantsInterfaceType {
    return {
      refresh: this.refresh,
      checkGrants: this.checkGrants,
      checkPendingGrants: this.checkPendingGrants,
      useReactCheckGrants: this.useReactCheckGrants,
      useReactCheckPendingGrants: this.useReactCheckPendingGrants
    };
  }

  private async _initialize(options?: GrantOptionsType) {
    const contextKey = options?.authContext ?? this.defaultKey;
    let promise = this.promiseHelper.getPendingPromise(contextKey);
    const isInitialized = this.initializedAuthContext.get(contextKey);

    if (!isInitialized) {
      this.initializedAuthContext.set(contextKey, true);
      if (!promise) {
        promise = this.refresh(options);
        this.promiseHelper.setPendingPromise(contextKey, promise);
      }
    }

    await promise;
  }

  private async refreshAction(options?: GrantOptionsType) {
    const { haveGrantsChanged, havePendingGrantsChanged } =
      await this.grantService.haveGrantsChanged(options);
    if (haveGrantsChanged) {
      void this.eventsService.publish(
        EventNames.shellGrantsChangedEventName,
        undefined
      );
    }
    if (havePendingGrantsChanged) {
      void this.eventsService.publish(
        EventNames.shellPendingGrantsChangedEventName,
        undefined
      );
    }
  }

  async refresh(options?: GrantOptionsType): Promise<void> {
    const promiseKey = options?.authContext ?? this.defaultKey;
    let promise = this.promiseHelper.getPendingPromise(promiseKey);
    if (this.grantService.isEnabled() && !promise) {
      promise = this.refreshAction(options);
      this.promiseHelper.setPendingPromise(promiseKey, promise);
    }
    await promise;
  }

  async checkGrants(
    grants: GrantType[],
    options?: GrantOptionsType
  ): Promise<boolean> {
    await this._initialize(options);
    return this.grantService.checkGrants(grants, options);
  }

  async checkPendingGrants(
    grants: GrantType[],
    options?: GrantOptionsType
  ): Promise<boolean> {
    await this._initialize(options);
    return this.grantService.checkPendingGrants(grants, options);
  }

  async getGrant(
    grant: GrantType,
    options?: GrantOptionsType
  ): Promise<GrantType> {
    await this._initialize(options);
    return this.grantService.getGrant(grant, options);
  }

  async getPendingGrant(
    pendingGrant: GrantType,
    options?: GrantOptionsType
  ): Promise<GrantType> {
    await this._initialize(options);
    return this.grantService.getPendingGrant(pendingGrant, options);
  }

  private useReactCheckBase<T>(
    _React: ReactLike,
    items: T,
    options: GrantOptionsType | undefined,
    checkMethod: (items: T, options?: GrantOptionsType) => Promise<boolean>,
    hookName: string,
    dependencyKey: string
  ): {
    data?: boolean;
    error?: Error;
    loading: boolean;
    reload: () => Promise<void>;
  } {
    const [result, setResult] = _React.useState({
      data: undefined,
      error: undefined,
      loading: true
    });

    const isMountedRef = _React.useRef(true);
    const dependenciesRef = _React.useRef({
      [dependencyKey]: items,
      options,
      handleDataCount: 0
    });
    dependenciesRef.current[dependencyKey] = items;
    dependenciesRef.current.options = options;

    const handleData = _React.useCallback(async () => {
      const thisCount = dependenciesRef.current.handleDataCount + 1;
      dependenciesRef.current.handleDataCount = thisCount;

      const newData = await checkMethod(
        dependenciesRef.current[dependencyKey],
        dependenciesRef.current.options
      )
        .then((data) => ({ data, error: undefined, loading: false }))
        .catch((error) => ({ data: undefined, error, loading: false }));

      if (!isMountedRef.current) {
        console.warn(`${hookName}: component unmounted before receiving data`);
      } else if (thisCount !== dependenciesRef.current.handleDataCount) {
        console.warn(
          `${hookName}: race condition detected, discarding stale data`
        );
      } else {
        setResult(newData);
      }
    }, []);

    _React.useEffect(() => {
      handleData();
    }, [
      handleData,
      JSON.stringify(items),
      options?.authContext,
      options?.offset
    ]);

    _React.useEffect(() => {
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    return _React.useMemo(
      () => ({
        ...result,
        reload: () => handleData()
      }),
      [result, handleData]
    );
  }

  public useReactCheckGrants(
    _React: ReactLike,
    grants: GrantType[],
    options?: GrantOptionsType
  ): {
    data?: boolean;
    error?: Error;
    loading: boolean;
    reload: () => Promise<void>;
  } {
    return this.useReactCheckBase(
      _React,
      grants,
      options,
      this.checkGrants.bind(this),
      'Grants.useReactCheckGrants',
      'grants'
    );
  }

  public useReactCheckPendingGrants(
    _React: ReactLike,
    pendingGrants: GrantType[],
    options?: GrantOptionsType
  ): {
    data?: boolean;
    error?: Error;
    loading: boolean;
    reload: () => Promise<void>;
  } {
    return this.useReactCheckBase(
      _React,
      pendingGrants,
      options,
      this.checkPendingGrants.bind(this),
      'Grants.useReactCheckPendingGrants',
      'pendingGrants'
    );
  }
}
